<template>
  <question-base-wrapper
    v-bind="baseWrapperProps"
  >
    <validation-provider
      v-slot="{ failed }"
      :vid="`${stepId}_${questionId}`"
      :name="questionId"
      :rules="fieldGenericRules"
      :data-unit-test="`${stepId}_${questionId}`"
    >
      <radio
        v-model="fieldGenericModel"
        :name="`${stepId}.${questionId}`"
        :items="fieldGenericItems"
        :error="failed"
        :error-messages="(!(hasOtherItems && isFieldOther) && failed) ? getFieldErrors('field', { required: true }) : []"
        :column="!row"
        :grid="grid"
        return-object
        :data-test="fieldTrackerId.field"
      />
    </validation-provider>

    <template :slot="otherOptionSlot">
      <form-row
        v-if="hasOtherItems && isFieldOther"
        gap="16"
        class="radio-list-other"
      >
        <validation-provider
          v-slot="{ failedRules, errors }"
          :vid="`${stepId}_${questionId}`"
          :name="questionId"
          :rules="fieldValidationRules"
        >
          <qz-select
            v-model="questionData[questionFields.field]"
            :items="fieldOtherItems"
            :error-messages="getFieldErrors('field', failedRules, errors)"
            :placeholder="helpers.getPlaceholderResponsiveText(optionsConfig.otherPlaceholder || 'Please select', isMobile)"
            :label="selectLabel"
            :data-test="fieldTrackerId.other"
          />
        </validation-provider>
      </form-row>
    </template>
  </question-base-wrapper>
</template>

<script>
import {
  pick,
  omit,
  snakeCase,
} from 'lodash';
import createOptions from '@/utils/createOptions';
import QuestionBase from './Base';
import QuestionBaseWrapper from './BaseWrapper';

const otherOption = {
  value: true,
  text: 'Other',
  logical: true,
  logicalValue: '',
};

export default {
  name: 'question-base-radio-list',
  extends: QuestionBase,
  components: { QuestionBaseWrapper },
  props: {
    convertKeysToNumbers: {
      type: Boolean,
      default: true,
    },
    otherOptionSlot: {
      type: String,
      default: 'subsection',
    },
    selectLabel: {
      type: String,
      default: '',
    },
    row: {
      type: Boolean,
      default: false,
    },
    grid: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return { fields: ['field'] };
  },
  computed: {
    fieldGenericItems () {
      if (!this.optionsConfig?.mainItems || this.optionsConfig?.mainItems.length === 0) {
        return createOptions({ source: this.optionsConfig.options, convertKeysToNumbers: this.convertKeysToNumbers });
      }

      if (Array.isArray(this.optionsConfig.options)) {
        const options = this.optionsConfig.options.filter(option => this.optionsConfig.mainItems.includes(option.value));

        return [
          ...options,
          Object.assign({}, otherOption, {
            text: this.optionsConfig.otherLabel || 'Other',
          }),
        ];
      }
      return [
        ...createOptions({ source: pick(this.optionsConfig.options, this.optionsConfig?.mainItems), convertKeysToNumbers: this.convertKeysToNumbers }),
        Object.assign({}, otherOption, {
          text: this.optionsConfig.otherLabel || 'Other',
        }),
      ];
    },
    fieldValidationRules () {
      if (this.validationRules) {
        return this.helpers.parseRules(this.validationRules?.field, this);
      }
      return 'listOptionRequired';
    },
    hasOtherItems () {
      return this.optionsConfig?.mainItems?.length > 0;
    },
    fieldGenericRules () {
      if (this.hasOtherItems) {
        return { is_not: { other: null } };
      }
      return 'required';
    },
    fieldOtherItems () {
      if (!this.hasOtherItems) return [];

      if (Array.isArray(this.optionsConfig.options)) {
        const options = this.optionsConfig.options.filter(option => !this.optionsConfig.mainItems.includes(option.value));

        return createOptions({ source: options, convertKeysToNumbers: this.convertKeysToNumbers });
      }

      return createOptions({ source: omit(this.optionsConfig.options, this.optionsConfig?.mainItems), convertKeysToNumbers: this.convertKeysToNumbers });
    },
    fieldGenericModel: {
      get () {
        if (this.questionData[this.questionFields.field] === null) return null;

        if (this.optionsConfig?.mainItems) {
          return this.optionsConfig.mainItems.includes(this.questionData[this.questionFields.field]) ? this.questionData[this.questionFields.field] : true;
        } else {
          return this.questionData[this.questionFields.field];
        }
      },
      set (val) {
        this.questionData[this.questionFields.field] = val.logical ? val.logicalValue : val.value;
      },
    },
    isFieldOther () {
      if (!this.optionsConfig?.mainItems.length || this.fieldGenericModel === null) return false;
      return !this.optionsConfig.mainItems.includes(this.fieldGenericModel);
    },
    fieldTrackerId () {
      return {
        field: snakeCase(this.questionFields.field),
        other: `${snakeCase(this.questionFields.field)}_other`,
      };
    },
  },
};
</script>
